import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { emptyReservationPartyDraft } from '../initialValues';
import type { ReservationFormChangeEvent } from '../Types/reservationFormChange';

const emptyOrganizationDraftFields = {
  organizationName: '',
  taxId: '',
  regon: '',
};

const emptyIndividualDraftFields = {
  firstName: '',
  lastName: '',
};

const getPartySelectorState = (previousDraft: typeof emptyReservationPartyDraft, value: unknown) =>
  value === '__new__'
    ? previousDraft
    : {
        ...emptyReservationPartyDraft,
      };

/**
 * Hook for handling changes in CreateReservation form fields.
 * Encapsulates all onChange logic – handles applicantId, applicant.*, participants, and others.
 */
export function useFormHandlers(
  setFormData: (fn: (prev: any) => any) => void,
  setSelectedApplicantId?: (id: number | null) => void
) {
  const handleChange = useCallback(
    (e: ReservationFormChangeEvent, _child?: ReactNode) => {
      const { name, value } = e.target;

      if (!name) {
        return;
      }

      if (name === 'applicantId') {
        setFormData((prev: any) => ({
          ...prev,
          applicantId: value,
        }));
        // Ustaw selectedApplicantId jeśli jest inny niż "__new__" i nie jest pusty
        if (setSelectedApplicantId && value && value !== '__new__') {
          setSelectedApplicantId(Number(value));
        } else if (setSelectedApplicantId) {
          setSelectedApplicantId(null);
        }
        return;
      }
      if (name === 'boardingPlaceId') {
        setFormData((prev: any) => ({
          ...prev,
          boardingPlaceId: value,
        }));
        return;
      }

      if (name.startsWith('applicant.')) {
        const field = name.split('.')[1];
        setFormData((prev: any) => ({
          ...prev,
          applicant: {
            ...prev.applicant,
            [field]: value,
          },
        }));
      } else if (name === 'applicantPartyId') {
        setFormData((prev: any) => ({
          ...prev,
          applicantPartyId: value,
          applicantPartyDraft: getPartySelectorState(prev.applicantPartyDraft, value),
        }));
      } else if (name === 'invoiceBuyerPartyId') {
        setFormData((prev: any) => ({
          ...prev,
          invoiceBuyerPartyId: value,
          invoiceBuyerPartyDraft: getPartySelectorState(prev.invoiceBuyerPartyDraft, value),
        }));
      } else if (name === 'fundingPartyId') {
        setFormData((prev: any) => ({
          ...prev,
          fundingPartyId: value,
          fundingPartyDraft: getPartySelectorState(prev.fundingPartyDraft, value),
        }));
      } else if (name.startsWith('applicantPartyDraft.')) {
        const field = name.split('.')[1];
        if (field === 'partyType') {
          setFormData((prev: any) => ({
            ...prev,
            applicantPartyDraft: {
              ...prev.applicantPartyDraft,
              ...(value === 'individual' ? emptyOrganizationDraftFields : emptyIndividualDraftFields),
              partyType: value,
            },
          }));
          return;
        }

        setFormData((prev: any) => ({
          ...prev,
          applicantPartyDraft: {
            ...prev.applicantPartyDraft,
            [field]: value,
          },
        }));
      } else if (name.startsWith('invoiceBuyerPartyDraft.')) {
        const field = name.split('.')[1];
        if (field === 'partyType') {
          setFormData((prev: any) => ({
            ...prev,
            invoiceBuyerPartyDraft: {
              ...prev.invoiceBuyerPartyDraft,
              ...(value === 'individual' ? emptyOrganizationDraftFields : emptyIndividualDraftFields),
              partyType: value,
            },
          }));
          return;
        }

        setFormData((prev: any) => ({
          ...prev,
          invoiceBuyerPartyDraft: {
            ...prev.invoiceBuyerPartyDraft,
            [field]: value,
          },
        }));
      } else if (name.startsWith('fundingPartyDraft.')) {
        const field = name.split('.')[1];
        if (field === 'partyType') {
          setFormData((prev: any) => ({
            ...prev,
            fundingPartyDraft: {
              ...prev.fundingPartyDraft,
              ...(value === 'individual' ? emptyOrganizationDraftFields : emptyIndividualDraftFields),
              partyType: value,
            },
          }));
          return;
        }

        setFormData((prev: any) => ({
          ...prev,
          fundingPartyDraft: {
            ...prev.fundingPartyDraft,
            [field]: value,
          },
        }));
      } else if (name === 'participants') {
        // Specjalny case dla dynamicList (cała tablica)
        setFormData((prev: any) => ({
          ...prev,
          participants: value,
          declaredParticipantsCount:
            Array.isArray(value) && value.length > 0
              ? value.length
              : prev.declaredParticipantsCount,
        }));
      } else if (name === 'selectedOfferOptions') {
        setFormData((prev: any) => ({
          ...prev,
          selectedOfferOptions: Array.isArray(value) ? value : [],
        }));
      } else if (name === 'declaredParticipantsCount') {
        const parsedValue = Number(value);
        setFormData((prev: any) => ({
          ...prev,
          declaredParticipantsCount:
            Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : undefined,
        }));
      } else {
        setFormData((prev: any) => ({
          ...prev,
          [name]: value,
        }));
      }
    },
    [setFormData, setSelectedApplicantId]
  );

  return { handleChange };
}
